import {type RefObject, useCallback, useState} from 'react'

/**
 * React useRef hook doesn't allow rerendering if reference has changed.
 * Use this hook to avoid this problem.
 *
 * @type T - type of the reference
 */
export function useControlledRef<T>() {
  'use no memo'
  // The React Compiler treats values returned from `useState` whose shape matches a
  // `MutableRefObject` as if they were stable refs, and caches the returned tuple
  // for the lifetime of the component. That causes consumers to always read the
  // initial `{current: null}` reference even after `setRef` has been called.

  const [ref, setRef] = useState<RefObject<T | null>>({current: null})

  const onRefChange = useCallback((next: T | null) => {
    setRef(s => (s.current === next ? s : {current: next}))
  }, [])

  return [ref, onRefChange] as const
}
